"use client";

import Image from "next/image";
import Link from "next/link";

export type LogoColorScheme = "light" | "dark" | "system";

interface LogoProps {
	iconOnly?: boolean;
	/**
	 * When true, renders the logo without a link wrapper.
	 * Useful when the parent already provides navigation (avoid nested `<a>`).
	 */
	disableLink?: boolean;
	/**
	 * Which wordmark assets to use. `system` follows Tailwind `dark` (current default).
	 * `light` always uses `logo_main.png`; `dark` always uses `logo_main_dark.png`.
	 * Icon uses `logo_icon.png` in all cases (single asset).
	 */
	colorScheme?: LogoColorScheme;
}

function LogoWordmark({ colorScheme }: { colorScheme: LogoColorScheme }) {
	if (colorScheme === "light") {
		return (
			<Image
				src="/images/logo_main.png"
				alt="SkillQuest"
				width={360}
				height={120}
				quality={100}
				className="h-8 w-auto"
			/>
		);
	}

	if (colorScheme === "dark") {
		return (
			<Image
				src="/images/logo_main_dark.png"
				alt="SkillQuest"
				width={360}
				height={120}
				quality={100}
				className="h-8 w-auto"
			/>
		);
	}

	return (
		<>
			<Image
				src="/images/logo_main_dark.png"
				alt="SkillQuest"
				width={360}
				height={120}
				quality={100}
				className="h-8 w-auto dark:hidden"
			/>
			<Image
				src="/images/logo_main.png"
				alt="SkillQuest"
				width={360}
				height={120}
				quality={100}
				className="hidden h-8 w-auto dark:block"
			/>
		</>
	);
}

function LogoIcon() {
	return (
		<Image
			src="/images/logo_icon.png"
			alt="SkillQuest"
			width={120}
			height={120}
			quality={100}
			className="h-8 w-auto"
		/>
	);
}

export function Logo({
	iconOnly = false,
	disableLink = false,
	colorScheme = "system",
}: LogoProps) {
	if (disableLink) {
		if (iconOnly) {
			return (
				<span className="flex items-center">
					<LogoIcon />
				</span>
			);
		}

		return (
			<span className="flex items-center">
				<LogoWordmark colorScheme={colorScheme} />
			</span>
		);
	}

	if (iconOnly) {
		return (
			<Link href="/" className="flex items-center">
				<LogoIcon />
			</Link>
		);
	}

	return (
		<Link href="/" className="flex items-center">
			<LogoWordmark colorScheme={colorScheme} />
		</Link>
	);
}
